import cn from 'classnames';
import React, { memo } from 'react';
import CheckboxField from '@framework/ui/Organisms/Filters/Checkboxer/CheckboxFieldContainer';
import FilterGroup from '@framework/ui/Organisms/Filters/components/FilterGroup';
import FilterItem from '@framework/ui/Organisms/Filters/components/FilterItem';
import FilterSection from '@framework/ui/Organisms/Filters/components/FilterSection';
import filtersStyles from '@framework/ui/Organisms/Filters/filters.module.scss';
import Checkbox from '@dealroadshow/uikit-checkbox';

interface IProps {
  items: any[],
  parentKeyPrefix: string,
  childrenKeyPrefix: string,
  sectionId: string,
  sectionTitle: string,
  isVisible: boolean,
  isExpanded: boolean,
  onSectionToggle: () => void,
  filterName: string,
  parentFilterName: string,
  isFilterChecked: (filterName: string, id: number) => boolean,
  handleParentFilterChange: (checkbox: any, name: string, parentIds: number[]) => void,
  handleFilterByChange: (filterName: string, ids: number[]) => void,
  toggleParentFilter: (checked: boolean, name: string, parentIdsWithoutChildren: number[]) => any,
  CheckboxComponent?: React.ElementType,
}

function RegionList(props: IProps) {
  const {
    sectionId,
    sectionTitle,
    isVisible,
    parentFilterName,
    filterName,
    parentKeyPrefix,
    childrenKeyPrefix,
    isExpanded,
    CheckboxComponent = Checkbox,
  } = props;
  const parentIds = props.items.map((item) => item[`${ parentKeyPrefix }Id`]);
  const parentIdsWithoutChildren = props.items
    .filter((parent) => !parent[`${ childrenKeyPrefix }List`]?.length)
    .map((item) => item[`${ parentKeyPrefix }Id`]);

  const handleParentChange = (hasChildren) => (checkbox) => {
    if (hasChildren) {
      props.handleFilterByChange(parentFilterName, [checkbox.name]);
    } else {
      props.handleParentFilterChange(checkbox, filterName, parentIds);
    }
  };

  const handleSectionChange = (checkbox) => {
    if (parentIdsWithoutChildren.length) {
      props.toggleParentFilter(checkbox.checked, parentFilterName, parentIdsWithoutChildren);
    }
    props.handleParentFilterChange(checkbox, filterName, parentIds);
  };

  return (
    <FilterSection
      sectionId={ sectionId }
      dataTest="filterPanelRegionListFilterSection"
      headerComponent={ (
        <CheckboxField
          name={ sectionId }
          dataTest={ sectionId }
          component={ CheckboxComponent }
          label={ sectionTitle }
          supportIndeterminate
          onChange={ handleSectionChange }
        />
      ) }
      isVisible={ isVisible }
      onSectionToggle={ props.onSectionToggle }
    >
      { props.items.map((parentFilterItem) => {
        const { canChangeResponse } = parentFilterItem;
        let childrenList = parentFilterItem[`${ childrenKeyPrefix }List`];
        let parentId = parentFilterItem[`${ parentKeyPrefix }Id`];
        let parentName = parentFilterItem[`${ parentKeyPrefix }Name`];
        let children = [];
        if (childrenList) {
          children = childrenList.map((child) => ({
            id: child[`${ childrenKeyPrefix }Id`],
            label: child[`${ childrenKeyPrefix }Name`],
            disabled: !child.canChangeResponse,
            checked: props.isFilterChecked(filterName, child[`${ childrenKeyPrefix }Id`]),
          }));
        }
        const isAllChildrenDisabled = Object.values(children)
          .every((checkbox) => checkbox.disabled);
        const activeChildrenCount = Object.values(children)
          .filter((checkbox) => !checkbox.disabled).length;
        const checkedChildrenCount = Object
          .values(children)
          .filter((checkbox) => !checkbox.disabled && checkbox.checked).length;
        const isDefaultCheckedParent = children.length
          ? (activeChildrenCount === checkedChildrenCount)
          : props.isFilterChecked(parentFilterName, parentId);

        return (
          <FilterGroup
            key={ parentId }
            groupId={ parentId }
            itemsCount={ activeChildrenCount }
            selectedItemsCount={ checkedChildrenCount }
            isExpanded={ isExpanded }
            checkboxComponent={ (
              <CheckboxField
                parent={ sectionId }
                name={ parentId }
                dataTest={ parentId }
                component={ CheckboxComponent }
                label={ parentName }
                supportIndeterminate={ !!children.length }
                disabled={ !canChangeResponse }
                onChange={ handleParentChange(!children.length) }
                defaultChecked={ isDefaultCheckedParent }
              />
            ) }
          >
            { children.map((child) => {
              const {
                id,
                label,
                disabled,
              } = child;
              return (
                <FilterItem
                  key={ id }
                  className={ cn(filtersStyles.filterItem, filtersStyles.filterNestedItem) }
                >
                  <CheckboxField
                    parent={ parentId }
                    name={ id }
                    dataTest={ id }
                    component={ CheckboxComponent }
                    label={ label }
                    supportCheckDisabled={ canChangeResponse && isAllChildrenDisabled }
                    disabled={ disabled }
                    onChange={ () => props.handleFilterByChange(filterName, [id]) }
                    defaultChecked={ props.isFilterChecked(filterName, id) }
                  />
                </FilterItem>
              );
            }) }
          </FilterGroup>
        );
      }) }
    </FilterSection>
  );
}

export default memo(RegionList);
